import * as _ from 'underscore'

import { ConvertHelper } from '../../../helpers'
import { CommonHelper } from '../../../helpers/common-helper'
import { EventsNames } from '../../../helpers/events-names'

import { SystemService } from '../../../services/system'

import { OpMarker, OpPolygon, OpRange, OpCoord, OpBounds, OpPolyline } from '../../../models/custom/op-map-area'
import { Enums } from '../../../models/common'

import { GoogleProvider } from './providers/google-provider'
import { Provider, ProviderOptions, ProviderScope, Map, MapEvent, MapLegendItem } from './providers/provider'
import { SmartgeomaticProvider } from './providers/smartgeomatic-provider'
import { StreetMapProvider } from './providers/streetmap-provider'
import { IMRBaseControlDirectiveController } from '../base'
import { IMRDataTypeLegedOptions, IMRDataTypeLegendIcon, IMRDataTypeLegendRect } from '../datatype-legend'

export { Map, MapEvent, MapLegendItem }


export class MapButtonClass {
    static areaVisiblityButton = 'glyphicon-adjust'
    static servicemansPopupButton = 'glyphicon-user'
}

export interface MapButton {
    /**
      * Use static field from MapButtonClass
      */
    class: string

    /**
      * On click event
      */
    onClick: () => void

    /**
      * Button tittle
      */
    title: string

    /**
      * Button visiblity
      */
    visible: boolean
}

export interface MapOptions extends ProviderOptions {
    provider: Enums.MapProvider

    onMapInit: (map: Map) => void

    // on area select event
    onAreaSelect?: (bounds: OpBounds) => void

    // Marker counter
    hideCounter?: boolean

    // Custom buttons
    customButtons?: MapButton[]

    // Legend - TODO: do poprawy, pozbyc sie any! https://app.asana.com/0/258335499755274/768906986368471
    legendItems?: MapLegendItem[]
    iconDict?: string[]

    // Legend visiblity
    hideLegend?: boolean

    // Close button visiblity
    hideCloseButton?: boolean

    hideToggleClusteringButton?: boolean
    onClusteringChange(isClustering: boolean): void
    isLegendVisible?: boolean
    onLegendVisiblityChange?(isLegendVisible: boolean): void

    isFiltered?: boolean
    resetFilter?: () => void

    onMapResize?: (isMapFullscreen: boolean) => void
}

interface MapDirectiveControllerScope extends ProviderScope {
    options: MapOptions

    markerCount: number

    elementsInTheArea: () => void
    enableSelectArea: () => void

    toggleClustering: () => void
    zoomCenter: () => void
    zoomIn: () => void

    showLegend: () => void
    legendData: IMRDataTypeLegedOptions

    isMapFullscreen: boolean
    isDisabled: boolean

    resizeMap: () => void
    closeMap: () => void

    resetFilter: () => void
}

export class MapDirectiveController extends IMRBaseControlDirectiveController {
    indexElement: number
    parentElement: JQuery
    provider: Provider
    selectAreaEnabled: boolean
    filterEnabled: boolean = true
    markers: OpMarker[] = []

    constructor(private $scope: MapDirectiveControllerScope, private $element: JQuery, $q: ng.IQService, private $timeout: ng.ITimeoutService, private systemService: SystemService) {
        super()

        $scope.markerCount = 0
        $scope.showZoomInButton = false

        this.initScopeMethod()
        $timeout(() => {
            this.initMap()
            this.initEvents()
        })
    }

    private initMap() {
        this.parentElement = this.$element.parent()
        let provider: Enums.MapProvider = Enums.MapProvider.GoogleMap
        const mapcontainer: HTMLElement = this.$element.find('.map-container').get(0)

        if (this.$scope.options.provider != null) {
            provider = this.$scope.options.provider
        }

        const onMapReady = (provider: Provider) => this.onMapReady(provider);

        if (provider === Enums.MapProvider.GoogleMap) {
            this.provider = new GoogleProvider(
                this.$scope,
                mapcontainer,
                onMapReady, this.systemService
            );
        } else if (provider === Enums.MapProvider.OpenStreetMap) {
            this.provider = new StreetMapProvider(
                this.$scope,
                mapcontainer,
                onMapReady,
                this.systemService
            )
        } else if (provider === Enums.MapProvider.SmartGeomatic) {
            this.provider = new SmartgeomaticProvider(
                this.$scope,
                mapcontainer,
                onMapReady,
                this.systemService
            )
        } else {
            throw new Error("Provider not implemented")
        }

        this.loadLegend(this.$scope.options.legendItems)
    }

    private onMapReady(provider: Provider = null) {
        if (this.provider == null) {
            this.provider = provider
        }

        let map: Map = {
            addMarker: (marker: OpMarker) => {
                return this.provider.addMarker(marker)
            },
            setMarkers: (markers: OpMarker[], zoom: boolean = false, center: boolean = false) => {
                this.markers = markers
                this.$scope.markerCount = markers.length
                this.provider.setMarkers(markers)

                if (zoom) {
                    this.$timeout(() => {
                        this.provider.zoomCenter()
                    }, 200)
                }
                else if (center) {
                    this.$timeout(() => {
                        this.provider.center();
                    }, 200)
                }
            },
            setSelectedMarkers: (markers: OpMarker[], animate: boolean, zoom: boolean = false) => {
                this.$scope.showZoomInButton = markers && markers.length > 0

                if (zoom) {
                    this.provider.zoomOnMarkers(markers, animate)
                }
                else {
                    this.provider.centerOnMarkers(markers, animate)
                }
            },
            addPolygon: (polygon: OpPolygon) => {
                return this.provider.addPolygon(polygon)
            },
            setPolygons: (polygons: OpPolygon[]) => {
                this.provider.setPolygons(polygons)
            },
            addRange: (range: OpRange) => {
                return this.provider.addRange(range)
            },
            setRanges: (ranges: OpRange[]) => {
                this.provider.setRanges(ranges)
            },
            addPolyline: (polyline: OpPolyline) => {
                return this.provider.addPolyline(polyline)
            },
            computeDistanceBetweenCoords: (c1: OpCoord, c2: OpCoord) => {
                return this.provider.computeDistanceBetweenCoords(c1, c2)
            },
            setCenter: (center: OpCoord) => {
                this.provider.setCenter(center)
            },
            setZoom: (zoom: number) => {
                this.provider.setZoom(zoom)
            },
            getZoom: () => {
                return this.provider.getZoom()
            },
            zoomCenter: () => {
                this.provider.zoomCenter()
            },
            zoomIn: () => {
                this.provider.zoomIn()
            },
            getBounds: () => {
                return this.provider.getBounds()
            },
            setBounds: (bounds: OpBounds) => {
                this.provider.setBounds(bounds)
            },
            fitBounds: (positions: OpCoord[]) => {
                this.provider.fitBounds(positions)
            },
            disableMap: () => {
                this.$scope.isDisabled = true
            },
            enableMap: () => {
                this.$scope.isDisabled = false
            },
            createMarkerTooltip: (marker: OpMarker, text: string) => {
                return this.provider.createMarkerTooltip(marker, text)
            },
            clearListeners: (event: MapEvent, marker: OpMarker) => {
                this.provider.clearListeners(event, marker)
            },
            setLegend: (legendItems: MapLegendItem[]) => {
                this.loadLegend(legendItems)
            },
            resize: () => {
                this.provider.resize()
            }
        }

        this.$scope.options.onMapInit(map)
    }

    private initScopeMethod() {
        this.$scope.toggleClustering = () => {
            this.$scope.options.isClustering = !this.$scope.options.isClustering
            this.provider.setMarkers(this.markers)
        }

        this.$scope.zoomCenter = () => {
            this.provider.zoomCenter()
        }

        this.$scope.zoomIn = () => {
            this.provider.zoomIn()
        }

        this.$scope.showLegend = () => {
            this.$scope.options.isLegendVisible = !this.$scope.options.isLegendVisible
        }

        this.$scope.resizeMap = () => {
            this.resizeMap()
        }

        this.$scope.closeMap = () => {
            this.$scope.$root.$broadcast(EventsNames.CLOSE_MAP)
        }

        this.$scope.elementsInTheArea = () => {
            if (this.$scope.options.onAreaSelect)
                this.$scope.options.onAreaSelect(this.provider.getBounds())
        }

        this.$scope.enableSelectArea = () => {
            this.selectAreaEnabled = !this.selectAreaEnabled
            if (this.selectAreaEnabled)
                this.provider.enableAreaSelect((bounds: OpBounds) => {
                    if (this.$scope.options.onAreaSelect)
                        this.$scope.options.onAreaSelect(bounds)

                    if (bounds) {
                        this.provider.setBounds(bounds)
                    }

                    this.selectAreaEnabled = false
                })
            else
                this.provider.disableAreaSelect()
        }

        this.$scope.resetFilter = () => {
            this.$scope.options.isFiltered = false
            if (this.$scope.options.resetFilter) {
                this.$scope.options.resetFilter()
            }
        }
    }

    private initEvents() {
        this.$scope.$on(EventsNames.MAP_CENTER, (event, ...args) => {
            this.provider.zoomCenter()
        })
    }

    //#region Legend

    private loadLegend(legendItems: MapLegendItem[]): void {
        const rects: IMRDataTypeLegendRect[] = []
        const icons: IMRDataTypeLegendIcon[] = []

        _.forEach(legendItems, (item: MapLegendItem) => {
            if (item.icon != null) {
                const icon: IMRDataTypeLegendIcon = this.getIconFromLegendItem(item)

                if (icon != null) {
                    icons.push(icon)
                }
            } else {
                const rect: IMRDataTypeLegendRect = this.getRectFromLegendItem(item)

                if (rect != null) {
                    rects.push(rect)
                }
            }
        })

        this.$scope.legendData = { icons, rects }
    }

    private getRectFromLegendItem(item: MapLegendItem): IMRDataTypeLegendRect {
        if (item != null) {
            return {
                description: item.description,
                color: angular.isString(item.color) ? item.color : CommonHelper.argbToRGB(item.color)
            }
        }

        return null
    }

    private getIconFromLegendItem(item: MapLegendItem): IMRDataTypeLegendIcon {
        const currentBrowser: Enums.Browsers = this.systemService.getBrowserName()

        if (item != null && item.icon != null) {
            if (item.icon.indexOf('data:image/') > -1) {
                return {
                    description: item.description,
                    imgSrc: item.icon
                }
            }
            else {
                if (currentBrowser === Enums.Browsers.InternetExplorer || currentBrowser === Enums.Browsers.MicrosoftEdge) {
                    const extension: string = ConvertHelper.getBase64EncodedFileExtension(item.icon.charAt(0))

                    return {
                        description: item.description,
                        imgSrc: 'data:image/' + extension + ';base64,' + item.icon
                    }
                }

                return {
                    description: item.description,
                    imgSrc: 'data:image/*;base64,' + item.icon
                }

            }
        }

        return null
    }


    //#endregion

    private resizeMap() {
        this.$scope.isMapFullscreen = !this.$scope.isMapFullscreen
        let currentBounds = this.provider.getBounds()
        let currentZoom = this.provider.getZoom()

        angular.element('.navbar-fixed-top').toggleClass("fixed")
        if (this.$scope.isMapFullscreen) {
            this.indexElement = this.$element.index()
            this.$element.appendTo('body')
        } else {
            if (this.indexElement === 0) {
                this.$element.prependTo(this.parentElement)
            } else {
                this.$element.insertAfter(this.parentElement.children().eq(this.indexElement - 1));
            }
        }
        this.provider.resize()
        this.$timeout(() => {
            this.provider.setBounds(currentBounds)
            this.provider.setZoom(currentZoom)
        }, 1000)

        if (_.isFunction(this.$scope.options.onMapResize)) {
            this.$scope.options.onMapResize(this.$scope.isMapFullscreen)
        }
    }
}